/**
 * useDubbingApi Hook
 *
 * Handles all dubbing API operations. Polling lifecycle is delegated to
 * GenerationContext so that job state survives tab switches and page reloads.
 * The hook's returned shape is identical to the pre-context version;
 * DubbingContext needs no changes.
 */

import { useCallback, useEffect, useRef } from 'react';
import { DubbingAPI } from '@/services/dubbing';
import { VideoGenerationAPI } from '@/services/api';
import type { DubbingRequest, DubbingResponse, DubbingProgress } from '@/types';
import type { TranslationParams } from '@/types/i18n';
import type { AudioLanguageConfig } from '@/components/common/LanguageSelector';
import type { DubbingState } from '../types';
import { toApiVoiceConfigs } from '../voiceConfigs';
import type { DubbingJobResult } from '@/services/polling/dubbingPollAdapter';
import { useGeneration } from '@/contexts/GenerationContext.context';
import { useRefreshResult } from '@/hooks/useGenerationJob';
import { handleError } from '@/utils/errorHandler';
import { downloadFile } from '@/utils/downloadProxy';
import { openVideoFolder } from '@/utils/electronBridge';

interface UseDubbingApiOptions {
  state: DubbingState;
  actions: {
    setMediaUrl: (url: string) => void;
    setUploadProgress: (progress: number) => void;
    setDubbingProgress: (progress: DubbingProgress | null) => void;
    setTaskId: (taskId: string) => void;
    setDownloadUrl: (url: string | null) => void;
    setLocalOutputPath: (path: string | null) => void;
    setDubbingResult: (result: DubbingResponse) => void;
    setAudioDubbingResult: (result: import('@/services/dubbing').AudioDubbingResponse) => void;
    setStatus: (
      status: 'idle' | 'uploading' | 'dubbing' | 'completed' | 'error',
      error?: string
    ) => void;
    startUploading: () => void;
    startDubbing: () => void;
    setAvailableModels: (
      models: Array<{ label: string; value: string; disabled?: boolean }>
    ) => void;
    setLlmModel: (model: string) => void;
    reset: () => void;
  };
  showToast: (type: 'error' | 'success' | 'info', title: string, message: string) => void;
  t: (
    key: string,
    defaultValueOrParams?: string | TranslationParams,
    params?: TranslationParams
  ) => string;
}

export function useDubbingApi({ state, actions, showToast, t }: UseDubbingApiOptions) {
  const { startJob, getLatestActiveByTab, getLatestResultByTab, cancelJob } = useGeneration();

  const activeJob = getLatestActiveByTab('dubbing');
  const resultJob = getLatestResultByTab('dubbing');
  const refreshResult = useRefreshResult<DubbingJobResult>(resultJob);

  // Sync GenerationContext job state back into DubbingState so the existing
  // UI components (DubbingProgress, DubbingResultsSection) continue to work
  // without any changes.
  useEffect(() => {
    if (!activeJob) return;

    const { status, progress, stage, message, timing } = activeJob;

    if (status === 'queued' || status === 'running') {
      if (stage && stage !== 'pending') {
        actions.setDubbingProgress({
          stage: stage as DubbingProgress['stage'],
          progress: progress ?? 0,
          message: message ?? t('dubbing.progress.processing', { defaultValue: 'Processing...' }),
          started_at: timing?.startedAt ?? null,
          elapsed_seconds: timing?.elapsedSeconds ?? null,
          estimated_remaining_seconds: timing?.estimatedRemainingSeconds ?? null,
        });
      }
    }
  }, [activeJob, actions, t]);

  // Handle terminal transitions from GenerationContext -- once per job and
  // status. Re-reading the finished job for fresh links (before a download)
  // replaces the job object; that is not a new transition, and re-running this
  // announced "dubbing completed" again on every download.
  const handledTransitionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!resultJob) return;
    const transition = `${resultJob.id}:${resultJob.status}`;
    if (handledTransitionRef.current === transition) return;

    if (resultJob.status === 'completed') {
      const jobResult = resultJob.result as DubbingJobResult | undefined;
      if (!jobResult) return;
      handledTransitionRef.current = transition;

      actions.setDubbingProgress({
        stage: 'completed',
        progress: 100,
        message: t('dubbing.progress.completed', { defaultValue: 'Dubbing completed!' }),
      });

      if (jobResult.kind === 'video' && jobResult.dubbingResult) {
        actions.setDubbingResult(jobResult.dubbingResult);
      } else if (jobResult.kind === 'audio' && jobResult.audioDubbingResult) {
        actions.setAudioDubbingResult(jobResult.audioDubbingResult);
      }

      actions.setStatus('completed');

      showToast(
        'success',
        t('dubbing.success.completedTitle'),
        t('dubbing.success.completedMessage')
      );
    } else if (
      resultJob.status === 'failed' ||
      resultJob.status === 'expired' ||
      resultJob.status === 'cancelled'
    ) {
      handledTransitionRef.current = transition;
      const errorMsg = resultJob.error?.message ?? 'Unknown error';
      actions.setDubbingProgress({
        stage: 'error',
        progress: 0,
        message: errorMsg,
        error: errorMsg,
      });
      actions.setStatus('error', errorMsg);
      showToast('error', t('dubbing.errors.failedTitle'), errorMsg);
    }
  }, [resultJob, actions, showToast, t]);

  // Keep downloadUrl and localOutputPath in sync with the result
  useEffect(() => {
    const updateDownloadUrl = async () => {
      const jobResult = resultJob?.result as DubbingJobResult | undefined;
      if (!jobResult) {
        actions.setDownloadUrl(null);
        actions.setLocalOutputPath(null);
        return;
      }

      // ONE url for both kinds. Reading a different field per kind is what
      // let the audio branch rot unnoticed for months.
      const rawUrl = jobResult.downloadUrl;
      actions.setLocalOutputPath(jobResult.localFilePath);

      if (!rawUrl) {
        actions.setDownloadUrl(null);
        return;
      }
      if (rawUrl.startsWith('http')) {
        actions.setDownloadUrl(rawUrl);
      } else {
        const baseUrl = await VideoGenerationAPI.getBaseUrl();
        actions.setDownloadUrl(`${baseUrl}${rawUrl}`);
      }
    };
    updateDownloadUrl();
  }, [resultJob, actions]);

  const startDubbing = useCallback(
    async (
      mediaFile: File,
      voiceConfigs: AudioLanguageConfig[],
      sourceLanguage: string,
      audioTrackMode: 'add' | 'overwrite',
      enableLlmRefinement: boolean,
      llmProvider: string,
      llmModel: string
    ) => {
      const processAudioDubbing = async (
        uploadedUrl: string,
        configs: AudioLanguageConfig[],
        apiConfigs: Array<{ language: string; provider: string; voice_id: string; speed: number }>
      ) => {
        const effectiveSourceLanguage =
          sourceLanguage && sourceLanguage !== 'auto' ? sourceLanguage : null;

        const response = await DubbingAPI.dubAudio({
          audio_url: uploadedUrl,
          source_language: effectiveSourceLanguage,
          target_languages: configs.map((c) => c.language),
          voice_configs: apiConfigs,
          timing_mode: 'synchronized',
          export_format: 'zip',
        });

        if (!response.success) {
          throw new Error(response.error ?? 'Failed to start audio dubbing');
        }

        actions.setTaskId(response.job_id);

        if (response.download_url) {
          // Synchronous result — resolved immediately, skip polling.
          actions.setDubbingProgress({
            stage: 'completed',
            progress: 100,
            message: t('dubbing.progress.completed', { defaultValue: 'Dubbing completed!' }),
          });
          actions.setAudioDubbingResult(response);
          actions.setStatus('completed');
          showToast(
            'success',
            t('dubbing.success.completedTitle'),
            t('dubbing.success.completedMessage')
          );
        } else if (response.job_id) {
          showToast(
            'success',
            t('dubbing.success.startedTitle'),
            t('dubbing.success.startedMessage')
          );
          startJob({
            id: response.job_id,
            tabMode: 'dubbing',
            serverType: 'dubbing',
            metadata: { kind: 'audio' },
            title: 'Audio dubbing',
          });
        } else {
          throw new Error('Invalid response: missing job_id and download_url');
        }
      };

      const processVideoDubbing = async (
        uploadedUrl: string,
        configs: AudioLanguageConfig[],
        apiConfigs: Array<{ language: string; provider: string; voice_id: string; speed: number }>
      ) => {
        // Video dubbing has NO auto-detect in the backend (the RunPod worker
        // rejects a missing source_language and the API rejects null/'auto').
        // DubbingContext validates before upload; this is a fail-fast safety net.
        if (!sourceLanguage || sourceLanguage === 'auto') {
          throw new Error(
            t('dubbing.errors.noSourceLanguageMessage', {
              defaultValue:
                'Video dubbing requires an explicit source language. Auto-detect is only available for audio files.',
            })
          );
        }

        const request: DubbingRequest = {
          video_url: uploadedUrl,
          source_language: sourceLanguage,
          target_languages: configs.map((c) => c.language),
          audio_track_mode: audioTrackMode,
          voice_configs: apiConfigs,
          enable_llm_refinement: enableLlmRefinement,
          ...(enableLlmRefinement && {
            llm_provider: llmProvider,
            llm_model: llmModel,
          }),
        };

        const response = await DubbingAPI.dubVideo(request);

        if (!response.success) {
          throw new Error(response.error ?? 'Failed to start dubbing');
        }

        // DubbingResponse.task_id holds the BE job_id (mapped in dubVideo())
        actions.setTaskId(response.task_id);

        if (response.download_url) {
          // Synchronous result — resolved immediately, skip polling.
          actions.setDubbingProgress({
            stage: 'completed',
            progress: 100,
            message: t('dubbing.progress.completed', { defaultValue: 'Dubbing completed!' }),
          });
          actions.setDubbingResult(response);
          actions.setStatus('completed');
          showToast(
            'success',
            t('dubbing.success.completedTitle'),
            t('dubbing.success.completedMessage')
          );
        } else if (response.task_id) {
          showToast(
            'success',
            t('dubbing.success.startedTitle'),
            t('dubbing.success.startedMessage')
          );
          startJob({
            id: response.task_id,
            tabMode: 'dubbing',
            serverType: 'dubbing',
            metadata: { kind: 'video' },
            title: 'Video dubbing',
          });
        } else {
          throw new Error('Invalid response: missing job_id and output_video_url');
        }
      };

      try {
        actions.startUploading();

        const { url: uploadedUrl, mediaType: detectedType } = await DubbingAPI.uploadMedia(
          mediaFile,
          (progress) => {
            actions.setUploadProgress(progress);
          }
        );

        actions.setMediaUrl(uploadedUrl);
        actions.startDubbing();
        actions.setDubbingProgress({
          stage: 'transcription',
          progress: 0,
          message: t('dubbing.progress.starting', { defaultValue: 'Starting dubbing process...' }),
        });

        // Same builder the cost card uses, so the quote and the job describe
        // one set of languages and one TTS provider (RULE #-2).
        const apiVoiceConfigs = toApiVoiceConfigs(voiceConfigs);

        if (detectedType === 'audio') {
          await processAudioDubbing(uploadedUrl, voiceConfigs, apiVoiceConfigs);
        } else {
          await processVideoDubbing(uploadedUrl, voiceConfigs, apiVoiceConfigs);
        }
      } catch (error) {
        const errorMessage = handleError(error, 'DubbingApi', 'Unknown error occurred');
        showToast('error', t('dubbing.errors.failedTitle'), errorMessage);
        actions.setStatus('error', errorMessage);
        actions.setDubbingProgress({
          stage: 'error',
          progress: 0,
          message: errorMessage,
          error: errorMessage,
        });
      }
    },
    [actions, showToast, t, startJob]
  );

  /**
   * Reveal the finished artifact in Finder/Explorer.
   *
   * Only reachable on the desktop build when the job produced a file on this
   * machine — `localOutputPath` comes from the backend, so a cloud job (whose
   * artifact lives in S3) never takes this path and keeps the HTTP download.
   */
  const handleOpenFolder = useCallback(async () => {
    if (!state.localOutputPath) {
      showToast('error', t('results.openFolder.errorTitle'), t('results.openFolder.noLocalPath'));
      return;
    }
    const opened = await openVideoFolder(state.localOutputPath);
    if (!opened) {
      showToast('error', t('results.openFolder.errorTitle'), t('results.openFolder.errorMessage'));
    }
  }, [state.localOutputPath, showToast, t]);

  const handleDownload = useCallback(async () => {
    if (!state.downloadUrl) return;

    const ext = state.mediaType === 'audio' ? 'zip' : 'mp4';
    const filename = `dubbed_${state.mediaType === 'audio' ? 'audio' : 'video'}_${state.taskId ?? 'download'}.${ext}`;

    // The download link is presigned and dies an hour after it was signed,
    // while the result stays on screen for a day. Re-read the job first (the
    // backend re-signs on every read); when that fails, say so rather than
    // hand the user the dead link held in state.
    let freshUrl: string | null;
    try {
      freshUrl = (await refreshResult()).downloadUrl;
    } catch (error) {
      console.error('[DubbingTab] Could not renew the download link:', error);
      showToast('error', t('result.linkRenewal.title'), t('result.linkRenewal.message'));
      return;
    }
    if (!freshUrl) {
      showToast('error', t('result.linkRenewal.title'), t('result.linkRenewal.message'));
      return;
    }

    try {
      await downloadFile(freshUrl, filename);
    } catch (error) {
      console.error('[DubbingTab] Failed to download:', error);
      // Opening the failed URL in a new window just showed the user the raw
      // JSON error body. Report the failure instead of staging it as a result.
      showToast(
        'error',
        t('dubbing.errors.downloadFailedTitle'),
        error instanceof Error ? error.message : String(error)
      );
    }
  }, [state.downloadUrl, state.mediaType, state.taskId, refreshResult, showToast, t]);

  const cancelDubbing = useCallback(() => {
    if (activeJob) {
      cancelJob(activeJob.id);
      DubbingAPI.cancelDubbing(activeJob.id);
    } else if (state.taskId) {
      DubbingAPI.cancelDubbing(state.taskId);
    }
    actions.reset();
  }, [activeJob, state.taskId, actions, cancelJob]);

  return {
    startDubbing,
    handleDownload,
    handleOpenFolder,
    cancelDubbing,
  };
}
